import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AudioCallRouteParam, AudioConferenceCall, AudioConferenceCalls, OperatorConferenceCall, OperatorConferenceCalls } from "../../../models/audioCall";
import { CallStatusType } from "../../../configs";


export type TSliceCall = {
  audioCallStartTime?: string;
  audioCallEndTime?: string;
  audioCallStatus?: AudioCallRouteParam;
  audioCallCall?: string;
  audioConferenceCalls?: AudioConferenceCalls;
  audioOperatorCalls?: OperatorConferenceCalls;
};

function InitSliceCall(): TSliceCall {
  return {
    audioCallStartTime: "",
    audioCallEndTime: "",
    audioCallStatus: {
      prev: 0,
      current: 0,
    },
    audioConferenceCalls: [],
    audioOperatorCalls: [],
  };
}

const sliceAudioCall = createSlice({
  name: "sliceAudioCall",
  initialState: InitSliceCall(),
  reducers: {
    SetAudioCallStartTime: (
      state,
      action: PayloadAction<string>
    ): TSliceCall => ({
      ...state,
      audioCallStartTime: action.payload,
    }),
    SetAudioCallEndTime: (
      state,
      action: PayloadAction<string>
    ): TSliceCall => ({
      ...state,
      audioCallEndTime: action.payload,
    }),

    SetAudioCallStatus: (
      state,
      action: PayloadAction<AudioCallRouteParam>
    ): TSliceCall => ({
      ...state,
      audioCallStatus: action.payload,
    }),

    UpsertAudioConferenceCall: (
      state,
      action: PayloadAction<AudioConferenceCall>
    ): TSliceCall => {
      const existingCalls = state.audioConferenceCalls || [];
      const nextOrder =
        existingCalls.reduce((max, call) => Math.max(max, call.callOrder ?? 0), 0) + 1;

      let index = -1;
      if (action.payload.callSid) {
        index = existingCalls.findIndex(
          (call) => call.callSid === action.payload.callSid
        );
      }
      if (index === -1) {
        index = existingCalls.findIndex(
          (call) => call.calledNumber === action.payload.calledNumber
        );
      }

      if (index !== -1) {
        const updatedCalls = [...existingCalls];
        const existingOrder = updatedCalls[index].callOrder;
        const shouldAssignOrder =
          existingOrder === undefined && action.payload.callOrder === undefined;
        updatedCalls[index] = {
          ...updatedCalls[index],
          ...action.payload,
          displayName: action.payload.displayName ?? updatedCalls[index].displayName,
          callOrder: action.payload.callOrder ?? (shouldAssignOrder ? nextOrder : existingOrder),
          lastUpdatedAt: new Date().toISOString(),
        };
        return {
          ...state,
          audioConferenceCalls: updatedCalls,
        };
      } else {
        return {
          ...state,
          audioConferenceCalls: [
            ...existingCalls,
            {
              ...action.payload,
              callOrder: action.payload.callOrder ?? nextOrder,
              lastUpdatedAt: new Date().toISOString(),
            },
          ],
        };
      }
    },

    UpdateAudioConferenceCallStatus: (
      state,
      action: PayloadAction<{
        calledNumber: string;
        current: CallStatusType;
        callSid: string; // <-- new
      }>
    ): TSliceCall => {
      const allowNumberOnlyStatuses = [
        CallStatusType.OutboundCallNotReceived,
        CallStatusType.OutboundCallNotAnswered,
        CallStatusType.Failed,
      ];

      if (!action.payload.callSid && !allowNumberOnlyStatuses.includes(action.payload.current)) {
        return state;
      }

      const existingCalls = state.audioConferenceCalls || [];
      const completedStatuses = new Set<CallStatusType>([
        CallStatusType.OutboundCallCompleted,
        CallStatusType.OutboundCallNotAnswered,
        CallStatusType.Failed,
      ]);
      const nextOrder =
        existingCalls.reduce((max, call) => Math.max(max, call.callOrder ?? 0), 0) + 1;
      const displayNameFromNumber = existingCalls.find(
        (call) => call.calledNumber === action.payload.calledNumber
      )?.displayName;
      const callOrderFromNumber = existingCalls.find(
        (call) => call.calledNumber === action.payload.calledNumber
      )?.callOrder;
      const dedupedCalls =
        action.payload.callSid
          ? existingCalls.filter((call) => {
              if (call.callSid === action.payload.callSid) return true;
              return call.calledNumber !== action.payload.calledNumber;
            })
          : existingCalls;

      const index = dedupedCalls.findIndex((call) => {
        if (action.payload.callSid) {
          return call.callSid === action.payload.callSid;
        }
        return call.calledNumber === action.payload.calledNumber;
      });

      const updatedCalls = [...dedupedCalls];

      if (index !== -1) {
        const existingCall = updatedCalls[index];
        const isCompleted = completedStatuses.has(action.payload.current);
        const statusChanged = existingCall.status?.current !== action.payload.current;

        updatedCalls[index] = {
          ...existingCall,
          callSid: action.payload.callSid ?? existingCall.callSid,
          calledNumber: action.payload.calledNumber ?? existingCall.calledNumber,
          displayName: existingCall.displayName ?? displayNameFromNumber,
          callOrder: isCompleted ? undefined : existingCall.callOrder ?? callOrderFromNumber ?? nextOrder,
          lastUpdatedAt: statusChanged ? new Date().toISOString() : existingCall.lastUpdatedAt,
          status: {
            prev: existingCall.status?.current ?? CallStatusType.Initiated,
            current: action.payload.current,
          },
        };
      } else {
        // Insert new call
        const isCompleted = completedStatuses.has(action.payload.current);
        updatedCalls.push({
          calledNumber: action.payload.calledNumber,
          callSid: action.payload.callSid,
          displayName: displayNameFromNumber,
          callOrder: isCompleted ? undefined : callOrderFromNumber ?? nextOrder,
          lastUpdatedAt: new Date().toISOString(),
          status: {
            prev: CallStatusType.Initiated,
            current: action.payload.current,
          },
        });
      }

      return {
        ...state,
        audioConferenceCalls: updatedCalls,
      };
    },

    ClearAudioConferenceCalls: (state) => ({
      ...state,
      audioConferenceCalls: [],
    }),
    RemoveAudioConferenceCallByCalledNumber: (
      state,
      action: PayloadAction<string>
    ): TSliceCall => {
      const existingCalls = state.audioConferenceCalls || [];
      return {
        ...state,
        audioConferenceCalls: existingCalls.filter(
          (call) => call.calledNumber !== action.payload
        ),
      };
    },

    UpsertOperatorConferenceCall: (
      state,
      action: PayloadAction<OperatorConferenceCall>
    ): TSliceCall => {
      const existingCalls = state.audioOperatorCalls || [];

      const index = existingCalls.findIndex(
        (call) => call.operatorId === action.payload.operatorId
      );

      if (index !== -1) {
        const updatedCalls = [...existingCalls];
        updatedCalls[index] = {
          ...updatedCalls[index],
          ...action.payload,
        };
        return {
          ...state,
          audioOperatorCalls: updatedCalls,
        };
      } else {
        return {
          ...state,
          audioOperatorCalls: [...existingCalls, action.payload],
        };
      }
    },

    UpdateOperatorConferenceCallStatus: (
      state,
      action: PayloadAction<{
        operatorId: string;
        current: CallStatusType;
      }>
    ): TSliceCall => {
      const existingCalls = state.audioOperatorCalls || [];

      const index = existingCalls.findIndex(
        (call) => call.operatorId === action.payload.operatorId
      );

      const updatedCalls = [...existingCalls];

      if (index !== -1) {
        const existingCall = updatedCalls[index];

        updatedCalls[index] = {
          ...existingCall,
          operatorStatus: {
            prev: existingCall.operatorStatus?.current ?? CallStatusType.Initiated,
            current: action.payload.current,
          },
        };
      } else {
        // Insert new call
        updatedCalls.push({
          operatorId: action.payload.operatorId,
          //the operator will be nameless
          // operatorName: action.payload.operatorName,
          operatorStatus: {
            prev: CallStatusType.Initiated,
            current: action.payload.current,
          },
        });
      }

      return {
        ...state,
        audioOperatorCalls: updatedCalls,
      };
    },


    ClearOperatorConferenceCalls: (state) => ({
      ...state,
      audioOperatorCalls: [],
    }),

    resetData: () => {
      return InitSliceCall();
    }
  },
});

export const ReducerAudioCall = sliceAudioCall.reducer;
export const ActionAudioCall = sliceAudioCall.actions;
